import type { ActivatedRouteSnapshot } from '@angular/router';

/**
 * Converts a router URL or `ActivatedRouteSnapshot` into a low-cardinality
 * route template suitable for monthly aggregation by page.
 *
 * Why this matters: persisting the raw URL would make `/patients/abc-123` and
 * `/patients/def-456` distinct rows. Aggregating "average load time per page"
 * would then produce one tiny-sample row per patient ID. We replace path
 * params with `:name` so the template `/patients/:id` aggregates across all
 * patients.
 *
 * Two inputs are supported:
 *   - `ActivatedRouteSnapshot` (preferred): walks the snapshot chain and
 *     rebuilds the path using each segment's route config `path`. This
 *     produces `/patients/:id` regardless of which patient was visited.
 *   - Raw URL string fallback: used when the snapshot isn't available (e.g.
 *     bootstrap span). Strips query/fragment and best-effort substitutes
 *     UUIDs / numeric / 24-hex object-id-shaped segments with `:id`.
 */
export class PageNameNormalizer {
  /** Best-effort identifier patterns we replace with `:id` in URL fallback. */
  private static readonly ID_PATTERNS: RegExp[] = [
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/, // UUID v1-5
    /^[0-9a-fA-F]{24}$/, // Mongo ObjectId-shaped
    /^\d+$/, // numeric ids
  ];

  /** Maximum length retained in the page_name column on the server (varchar 100). */
  private static readonly MAX_LENGTH = 100;

  public fromRouteSnapshot(rootSnapshot: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | null = rootSnapshot;
    // Walk to the deepest activated child (Angular nests them under `firstChild`).
    while (node?.firstChild) {
      node = node.firstChild;
    }
    // Now walk back up collecting `routeConfig.path` from each ancestor that
    // contributes a segment. Skipping nodes with no path gives us a clean template.
    let cursor: ActivatedRouteSnapshot | null = node;
    while (cursor) {
      const cfgPath = cursor.routeConfig?.path;
      if (cfgPath !== undefined && cfgPath !== '' && cfgPath !== null) {
        segments.unshift(cfgPath);
      }
      cursor = cursor.parent;
    }
    if (segments.length === 0) {
      return '/';
    }
    const joined = '/' + segments.filter((s) => s.length > 0).join('/');
    return this.truncate(joined.replace(/\/+/g, '/'));
  }

  public fromUrl(url: string): string {
    const stripped = this.stripQueryAndFragment(url);
    if (stripped === '' || stripped === '/') {
      return '/';
    }
    const parts = stripped.split('/').filter((p) => p.length > 0);
    const replaced = parts.map((p) => (PageNameNormalizer.ID_PATTERNS.some((rx) => rx.test(p)) ? ':id' : p));
    return this.truncate('/' + replaced.join('/'));
  }

  private stripQueryAndFragment(url: string): string {
    const q = url.indexOf('?');
    const f = url.indexOf('#');
    let end = url.length;
    if (q !== -1) {
      end = Math.min(end, q);
    }
    if (f !== -1) {
      end = Math.min(end, f);
    }
    return url.slice(0, end);
  }

  private truncate(value: string): string {
    if (value.length <= PageNameNormalizer.MAX_LENGTH) {
      return value;
    }
    return value.slice(0, PageNameNormalizer.MAX_LENGTH);
  }
}
